import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Strips every existing <meta name="robots"> and <meta name="googlebot">
 * tag from <head>, then injects a single `noindex, nofollow` pair.
 * Returns a cleanup fn that removes only the tags we added, leaving the
 * original state restored on unmount.
 */
function applyNoIndex() {
  const removed = [];
  document.head.querySelectorAll('meta[name="robots"], meta[name="googlebot"]').forEach(el => {
    removed.push({ node: el.cloneNode(true), position: Array.from(document.head.children).indexOf(el) });
    el.remove();
  });
  const robotsMeta = document.createElement('meta');
  robotsMeta.name = 'robots';
  robotsMeta.content = 'noindex, nofollow';
  robotsMeta.setAttribute('data-dynamic-noindex', 'true');
  document.head.appendChild(robotsMeta);

  const googlebotMeta = document.createElement('meta');
  googlebotMeta.name = 'googlebot';
  googlebotMeta.content = 'noindex, nofollow';
  googlebotMeta.setAttribute('data-dynamic-noindex', 'true');
  document.head.appendChild(googlebotMeta);

  return () => {
    document.head.querySelectorAll('meta[data-dynamic-noindex="true"]').forEach(el => el.remove());
    removed.forEach(({ node }) => document.head.appendChild(node));
  };
}

function NotFound() {
  useEffect(() => {
    const restore = applyNoIndex();
    const prevTitle = document.title;
    document.title = '404 — Page Not Found | Is It Taxed';
    return () => {
      restore();
      document.title = prevTitle;
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4 transition-colors"
      data-testid="not-found-page"
    >
      <div className="max-w-md w-full text-center">
        <div className="inline-block bg-gradient-to-b from-yellow-300 to-yellow-500 px-8 py-3 rounded-lg border-4 border-gray-900 shadow-xl mb-8">
          <p className="text-4xl font-black tracking-widest text-gray-900 font-mono">404</p>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
          Page not found
        </h1>
        <p className="text-base text-gray-600 dark:text-gray-400 mb-8">
          The page you’re looking for doesn’t exist or has moved. Try checking a vehicle or browsing our guides.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/">
            <Button
              className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-semibold w-full sm:w-auto"
              data-testid="notfound-home-btn"
            >
              <Home className="mr-2 h-4 w-4" />
              Go home
            </Button>
          </Link>
          <Link to="/blog">
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              data-testid="notfound-blog-btn"
            >
              <Search className="mr-2 h-4 w-4" />
              Browse guides
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export { applyNoIndex };
export default NotFound;
